import { Link } from "react-router-dom";
import type { Article } from "@/types/content";

interface ArticleCardProps {
  article: Article;
  variant?: "grid" | "row" | "minimal";
}

export default function ArticleCard({ article, variant = "grid" }: ArticleCardProps) {
  if (variant === "row") {
    return (
      <article className="group flex gap-4 border-b border-background-200 py-4 last:border-b-0">
        <Link
          to={`/article/${article.id}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-24 sm:w-36"
        >
          <img
            src={article.image}
            alt={article.imageAlt}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 flex-1">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors hover:text-primary-700"
          >
            {article.title}
          </Link>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="text-accent-700">{article.topicName}</span>
            <span>{article.date}</span>
            <span>{article.readTime}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {article.views}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "minimal") {
    return (
      <article className="group flex items-start gap-3">
        <span className="mt-1 font-heading text-lg font-bold text-primary-300 transition-colors group-hover:text-primary-500">
          <i className="ri-vip-crown-2-line"></i>
        </span>
        <div className="min-w-0">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 text-sm font-medium leading-relaxed text-foreground-800 transition-colors hover:text-primary-700"
          >
            {article.title}
          </Link>
          <p className="mt-1 text-xs text-foreground-500">
            {article.topicName} · {article.views} 阅读
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/article/${article.id}`}
        className="relative block h-44 w-full overflow-hidden bg-background-100"
      >
        <img
          src={article.image}
          alt={article.imageAlt}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
          {article.topicName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <Link
          to={`/article/${article.id}`}
          className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors hover:text-primary-700"
        >
          {article.title}
        </Link>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {article.views}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}